import { MobXProviderContext } from "mobx-react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { getErrorTitle } from "../../../constants/constants";
import { appSectionCardStyle } from "../../../components/general/uiSurfaceStyles";

const TIMEZONE = "America/Los_Angeles";
const activityOptions = [
  { label: "Smoking", value: "smoking" },
  { label: "Laundry", value: "laundry" },
  { label: "Room Deep Clean", value: "room_cleaning" },
  { label: "Shower", value: "shower" },
  { label: "Transportation", value: "transportation" },
  { label: "Appointment", value: "appointment" },
  { label: "Wellness Check", value: "wellness_check" },
  { label: "Custom Activity", value: "custom" },
];
const weekDayOptions = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
  .map((label, value) => ({ label, value }));

const getLosAngelesDateValue = (value: Date) => new Intl.DateTimeFormat("en-CA", {
  timeZone: TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
}).format(value);

const getTodayDateValue = () => getLosAngelesDateValue(new Date());

const getStatusSeverity = (status: string): "success" | "info" | "warning" | "danger" | "secondary" => {
  if (status === "completed") return "success";
  if (status === "missed") return "danger";
  if (status === "due") return "warning";
  if (status === "skipped") return "secondary";
  return "info";
};

const ResidentSchedulesPage = () => {
  const { homeId = "", residentId = "" } = useParams<{ homeId: string; residentId: string }>();
  const { backendAPIStore } = useContext(MobXProviderContext);
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduleData, setScheduleData] = useState<any>({ schedules: [], occurrences: [], summary: null });
  const [staffOptions, setStaffOptions] = useState<any[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<any>(null);
  const [form, setForm] = useState<any>({
    activityType: "custom",
    activityName: "",
    instructions: "",
    recurrenceType: "daily",
    daysOfWeek: [],
    scheduledTime: "09:00",
    startDate: getTodayDateValue(),
    endDate: "",
    assignedStaffIds: [],
    backupStaffIds: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await backendAPIStore.init();
      const [scheduleResponse, staffResponse] = await Promise.all([
        backendAPIStore.getResidentSchedules(residentId),
        homeId ? backendAPIStore.getLinkedCaregiversForHome(homeId).catch(() => []) : Promise.resolve([]),
      ]);
      setScheduleData(scheduleResponse || { schedules: [], occurrences: [], summary: null });
      const staff = Array.isArray(staffResponse) ? staffResponse : Array.isArray(staffResponse?.items) ? staffResponse.items : [];
      setStaffOptions(staff.map((item: any) => ({
        label: `${item?.firstName || ""} ${item?.lastName || ""}`.trim() || item?.email || item?.name || "Staff",
        value: String(item?.caregiverId || item?.staffId || item?.userId || item?.id || "").trim(),
      })).filter((item: any) => item.value));
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load schedules.", life: 3500 });
    } finally {
      setLoading(false);
    }
  }, [backendAPIStore, homeId, residentId]);

  useEffect(() => { void load(); }, [load]);

  const openNew = () => {
    setEditingSchedule(null);
    setForm({ activityType: "custom", activityName: "", instructions: "", recurrenceType: "daily", daysOfWeek: [], scheduledTime: "09:00", startDate: getTodayDateValue(), endDate: "", assignedStaffIds: [], backupStaffIds: [] });
    setShowDialog(true);
  };

  const openEdit = (schedule: any) => {
    setEditingSchedule(schedule);
    setForm({ ...schedule, daysOfWeek: Array.isArray(schedule?.daysOfWeek) ? schedule.daysOfWeek : [], assignedStaffIds: Array.isArray(schedule?.assignedStaffIds) ? schedule.assignedStaffIds : [], backupStaffIds: Array.isArray(schedule?.backupStaffIds) ? schedule.backupStaffIds : [] });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.activityName.trim() || !form.startDate || !form.scheduledTime) {
      toast.current?.show({ severity: "warn", summary: "Required fields", detail: "Activity name, start date, and time are required.", life: 3000 });
      return;
    }
    setSaving(true);
    try {
      await backendAPIStore.init();
      const response = editingSchedule
        ? await backendAPIStore.updateResidentSchedule({ ...form, residentId, scheduleId: editingSchedule.scheduleId })
        : await backendAPIStore.createResidentSchedule({ ...form, residentId });
      setScheduleData(response || scheduleData);
      setShowDialog(false);
      await load();
      toast.current?.show({ severity: "success", summary: "Schedule saved", detail: "The resident schedule was updated.", life: 2500 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save schedule.", life: 3500 });
    } finally {
      setSaving(false);
    }
  };

  const completeOccurrence = async (occurrence: any, action: "complete" | "skip") => {
    try {
      await backendAPIStore.init();
      const response = action === "complete"
        ? await backendAPIStore.completeResidentSchedule({ residentId, scheduleId: occurrence.scheduleId, occurrenceDate: String(occurrence.scheduledAt || "").slice(0, 10) })
        : await backendAPIStore.skipResidentSchedule({ residentId, scheduleId: occurrence.scheduleId, occurrenceDate: String(occurrence.scheduledAt || "").slice(0, 10) });
      setScheduleData(response || scheduleData);
      await load();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update activity.", life: 3500 });
    }
  };

  const endSchedule = async (schedule: any) => {
    try {
      await backendAPIStore.init();
      await backendAPIStore.endResidentSchedule(residentId, schedule.scheduleId);
      await load();
      toast.current?.show({ severity: "success", summary: "Schedule ended", detail: "The schedule will no longer create activities.", life: 2500 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to end schedule.", life: 3500 });
    }
  };

  const scheduleRows = useMemo(() => Array.isArray(scheduleData?.schedules) ? scheduleData.schedules : [], [scheduleData]);
  const occurrenceRows = useMemo(() => Array.isArray(scheduleData?.occurrences) ? scheduleData.occurrences : [], [scheduleData]);
  const staffTemplate = (value: string[]) => (value || []).map((id) => staffOptions.find((staff) => staff.value === id)?.label || id).join(", ") || "Unassigned";

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <div className="flex align-items-center justify-content-between gap-2 flex-wrap mb-3">
        <div>
          <Button type="button" label="Back" icon="pi pi-arrow-left" text onClick={() => navigate(-1)} />
          <h2 className="mt-2 mb-1" style={{ color: "#0f172a" }}>Resident Schedules</h2>
          <div style={{ color: "#64748b" }}>All times use {TIMEZONE}.</div>
        </div>
        <Button type="button" label="Add Schedule" icon="pi pi-plus" severity="success" rounded onClick={openNew} />
      </div>
      {loading ? <div className="flex justify-content-center p-5"><ProgressSpinner /></div> : (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            <Tag value={`Active ${scheduleData?.summary?.activeCount || 0}`} severity="info" />
            <Tag value={`Due ${scheduleData?.summary?.dueCount || 0}`} severity="warning" />
            <Tag value={`Missed ${scheduleData?.summary?.missedCount || 0}`} severity="danger" />
          </div>
          <div style={appSectionCardStyle} className="mb-3">
            <h3 className="mt-0">Today’s Activities</h3>
            <DataTable value={occurrenceRows} emptyMessage="No scheduled activities today." responsiveLayout="scroll">
              <Column field="activityName" header="Activity" />
              <Column field="scheduledAt" header="Scheduled" body={(row: any) => new Date(row.scheduledAt).toLocaleString("en-US", { timeZone: TIMEZONE, dateStyle: "medium", timeStyle: "short" })} />
              <Column header="Assigned Staff" body={(row: any) => staffTemplate(row.assignedStaffIds)} />
              <Column header="Status" body={(row: any) => <Tag value={row.status} severity={getStatusSeverity(row.status)} />} />
              <Column header="Actions" body={(row: any) => row.status === "completed" || row.status === "skipped" ? null : <div className="flex gap-2"><Button type="button" label="Complete" size="small" severity="success" onClick={() => void completeOccurrence(row, "complete")} /><Button type="button" label="Skip" size="small" severity="secondary" outlined onClick={() => void completeOccurrence(row, "skip")} /></div>} />
            </DataTable>
          </div>
          <div style={appSectionCardStyle}>
            <div className="flex align-items-center justify-content-between gap-2 flex-wrap"><h3 className="mt-0">Schedule Definitions</h3></div>
            <DataTable value={scheduleRows} emptyMessage="No schedules configured." responsiveLayout="scroll">
              <Column field="activityName" header="Activity" />
              <Column field="recurrenceType" header="Repeats" />
              <Column field="scheduledTime" header="Time" />
              <Column header="Assigned Staff" body={(row: any) => staffTemplate(row.assignedStaffIds)} />
              <Column header="Status" body={(row: any) => <Tag value={row.status} severity={row.status === "active" ? "success" : "secondary"} />} />
              <Column header="Actions" body={(row: any) => <div className="flex gap-2"><Button type="button" label="Edit" icon="pi pi-pencil" size="small" outlined onClick={() => openEdit(row)} />{row.status === "active" ? <Button type="button" label="End" icon="pi pi-stop" size="small" severity="danger" outlined onClick={() => void endSchedule(row)} /> : null}</div>} />
            </DataTable>
          </div>
        </>
      )}
      <Dialog visible={showDialog} modal header={editingSchedule ? "Edit Schedule" : "Add Schedule"} style={{ width: "38rem", maxWidth: "96vw" }} onHide={() => !saving && setShowDialog(false)} footer={<div className="flex justify-content-end gap-2"><Button type="button" label="Cancel" severity="secondary" outlined onClick={() => setShowDialog(false)} disabled={saving} /><Button type="button" label="Save" icon="pi pi-save" severity="success" onClick={() => void save()} loading={saving} /></div>}>
        <div className="flex flex-column gap-3">
          <div><label className="block mb-2">Activity type</label><Dropdown value={form.activityType} options={activityOptions} className="w-full" onChange={(event) => setForm({ ...form, activityType: event.value, activityName: form.activityName || activityOptions.find((item) => item.value === event.value)?.label || "" })} /></div>
          <div><label className="block mb-2">Activity name</label><InputText value={form.activityName} className="w-full" onChange={(event) => setForm({ ...form, activityName: event.target.value })} /></div>
          <div><label className="block mb-2">Instructions</label><InputTextarea value={form.instructions} className="w-full" rows={3} onChange={(event) => setForm({ ...form, instructions: event.target.value })} /></div>
          <div className="grid"><div className="col-6"><label className="block mb-2">Repeats</label><Dropdown value={form.recurrenceType} options={[{ label: "Daily", value: "daily" }, { label: "Weekly", value: "weekly" }]} className="w-full" onChange={(event) => setForm({ ...form, recurrenceType: event.value })} /></div><div className="col-6"><label className="block mb-2">Time</label><InputText type="time" value={form.scheduledTime} className="w-full" onChange={(event) => setForm({ ...form, scheduledTime: event.target.value })} /></div></div>
          {form.recurrenceType === "weekly" ? <div><label className="block mb-2">Days</label><MultiSelect value={form.daysOfWeek} options={weekDayOptions} className="w-full" display="chip" onChange={(event) => setForm({ ...form, daysOfWeek: event.value })} /></div> : null}
          <div className="grid"><div className="col-6"><label className="block mb-2">Start date</label><Calendar value={form.startDate ? new Date(`${form.startDate}T12:00:00`) : null} showIcon dateFormat="yy-mm-dd" className="w-full" onChange={(event) => setForm({ ...form, startDate: event.value ? getLosAngelesDateValue(event.value as Date) : "" })} /></div><div className="col-6"><label className="block mb-2">End date</label><Calendar value={form.endDate ? new Date(`${form.endDate}T12:00:00`) : null} showIcon dateFormat="yy-mm-dd" className="w-full" onChange={(event) => setForm({ ...form, endDate: event.value ? getLosAngelesDateValue(event.value as Date) : "" })} /></div></div>
          <div><label className="block mb-2">Assigned caregivers</label><MultiSelect value={form.assignedStaffIds} options={staffOptions} className="w-full" display="chip" placeholder="Select staff" onChange={(event) => setForm({ ...form, assignedStaffIds: event.value })} /></div>
          <div><label className="block mb-2">Backup caregivers</label><MultiSelect value={form.backupStaffIds} options={staffOptions} className="w-full" display="chip" placeholder="Select backup staff" onChange={(event) => setForm({ ...form, backupStaffIds: event.value })} /></div>
        </div>
      </Dialog>
    </div>
  );
};

export default ResidentSchedulesPage;
